/**
 * Announcement Detail Page
 * Mobile-responsive view for individual announcements with role-based features
 * Optimized for mobile devices, tablets, and desktops with touch-friendly interfaces
 */

import React, { useCallback, useState, useEffect } from 'react';

import {
  Eye,
  Edit,
  Trash2,
  Send,
  CheckCircle,
  XCircle,
  Clock,
  Users,
  Mail,
  MessageSquare,
  Bell,
  AlertTriangle,
  Calendar,
  Tag,
  Building,
  ArrowLeft,
  Share2,
} from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AnnouncementForm } from '../../components/announcements/AnnouncementForm';
import { Button } from '../../components/common/Button';
import { Card, CardHeader, CardContent } from '../../components/common/Card';
import { Modal } from '../../components/modals/Modal';
import {
  approveAnnouncement,
  broadcastAnnouncement,
  deleteAnnouncement,
  getAnnouncement,
  updateAnnouncement,
} from '../../services/announcementsService';
import { useAppSelector } from '../../store/hooks';
import { Announcement, AnnouncementUpdateFormData , UserRole } from '../../types';
import { cn, formatDate } from '../../utils';


interface AnnouncementDetailPageProps {
  className?: string;
}

export const AnnouncementDetailPage: React.FC<AnnouncementDetailPageProps> = ({
  className,
}) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAppSelector(state => state.auth);

  const [announcement, setAnnouncement] = useState<Announcement | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isAdmin = [
    UserRole.DISTRICT_ADMIN,
    UserRole.SCHOOL_ADMIN,
    UserRole.EVALUATOR,
  ].includes(user?.role as UserRole);

  const isSent = announcement?.status === 'sent';
  const canEdit = !isSent && (isAdmin || announcement?.created_by === user?.id);
  const canDelete = isAdmin && !isSent;
  const canBroadcast = isAdmin && announcement?.status === 'approved';
  const canApprove = isAdmin && announcement?.status === 'pending_approval';

  const loadAnnouncement = useCallback(async () => {
    if (!id) return;

    try {
      setLoading(true);
      setError(null);

      setAnnouncement(await getAnnouncement(id));
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load announcement'
      );
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadAnnouncement();
  }, [loadAnnouncement]);

  const handleEdit = () => {
    setShowEditModal(true);
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleFormSubmit = async (data: AnnouncementUpdateFormData) => {
    if (!announcement) return;

    try {
      setIsSubmitting(true);

      setAnnouncement(await updateAnnouncement(announcement.id, data));
      setShowEditModal(false);
      toast.success('Announcement updated successfully');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to update announcement'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!announcement) return;

    try {
      setIsSubmitting(true);

      await deleteAnnouncement(announcement.id);
      toast.success('Announcement deleted successfully');
      navigate('/announcements');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to delete announcement'
      );
    } finally {
      setIsSubmitting(false);
      setShowDeleteModal(false);
    }
  };

  const handleBroadcast = async () => {
    if (!announcement) return;

    try {
      setIsSubmitting(true);

      await broadcastAnnouncement(announcement.id);
      await loadAnnouncement();
      toast.success('Announcement broadcasted successfully');
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Failed to broadcast announcement'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleApprove = async (decision: 'approved' | 'rejected') => {
    if (!announcement) return;

    try {
      setIsSubmitting(true);

      await approveAnnouncement(announcement.id, decision);
      await loadAnnouncement();
      toast.success(`Announcement ${decision} successfully`);
    } catch (err) {
      toast.error(
        err instanceof Error
          ? err.message
          : `Failed to ${decision} announcement`
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft':
        return 'text-gray-600 bg-gray-100';
      case 'pending_approval':
        return 'text-yellow-600 bg-yellow-100';
      case 'approved':
        return 'text-green-600 bg-green-100';
      case 'scheduled':
        return 'text-blue-600 bg-blue-100';
      case 'sent':
        return 'text-purple-600 bg-purple-100';
      case 'failed':
        return 'text-red-600 bg-red-100';
      case 'cancelled':
        return 'text-gray-600 bg-gray-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'low':
        return 'text-green-600 bg-green-100';
      case 'medium':
        return 'text-yellow-600 bg-yellow-100';
      case 'high':
        return 'text-orange-600 bg-orange-100';
      case 'critical':
        return 'text-red-600 bg-red-100';
      default:
        return 'text-gray-600 bg-gray-100';
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'emergency':
        return <AlertTriangle className="w-4 h-4" />;
      case 'event':
        return <Calendar className="w-4 h-4" />;
      case 'academic':
        return <Users className="w-4 h-4" />;
      case 'policy':
        return <Building className="w-4 h-4" />;
      case 'reminder':
        return <Bell className="w-4 h-4" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  const getChannelIcon = (channel: string) => {
    switch (channel) {
      case 'email':
        return <Mail className="w-4 h-4" />;
      case 'sms':
        return <MessageSquare className="w-4 h-4" />;
      case 'push':
        return <Bell className="w-4 h-4" />;
      case 'in_app':
        return <Eye className="w-4 h-4" />;
      case 'banner':
        return <Share2 className="w-4 h-4" />;
      default:
        return <MessageSquare className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="text-center py-6 sm:py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading announcement...</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error || !announcement) {
    return (
      <Card>
        <CardContent className="p-4 sm:p-6">
          <div className="text-center py-6 sm:py-8">
            <div className="text-red-500 mb-4">
              <svg
                className="w-12 h-12 mx-auto"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Error Loading Announcement
            </h3>
            <p className="text-gray-600 mb-4 text-sm sm:text-base">
              {error || 'Announcement not found'}
            </p>
            <Button
              variant="primary"
              onClick={() => navigate('/announcements')}
              className="min-h-[44px]"
            >
              Back to Announcements
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className={cn('space-y-4 sm:space-y-6', className)}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center space-x-3">
          <Button
            variant="ghost"
            onClick={() => navigate('/announcements')}
            className="!rounded-full w-10 h-10 bg-white"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              {announcement.title}
            </h1>
            <div className="flex items-center space-x-2 mt-1">
              <span
                className={cn(
                  'px-2 py-1 rounded-full text-xs font-medium',
                  getStatusColor(announcement.status)
                )}
              >
                {announcement.status.replace('_', ' ').toUpperCase()}
              </span>
              <span
                className={cn(
                  'px-2 py-1 rounded-full text-xs font-medium',
                  getPriorityColor(announcement.priority)
                )}
              >
                {announcement.priority.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center space-x-2">
          {canApprove && (
            <>
              <Button
                variant="primary"
                onClick={() => handleApprove('approved')}
                disabled={isSubmitting}
                className="min-h-[44px]"
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Approve
              </Button>
              <Button
                variant="secondary"
                onClick={() => handleApprove('rejected')}
                disabled={isSubmitting}
                className="min-h-[44px]"
              >
                <XCircle className="w-4 h-4 mr-2" />
                Reject
              </Button>
            </>
          )}

          {canBroadcast && (
            <Button
              variant="primary"
              onClick={handleBroadcast}
              disabled={isSubmitting}
              className="min-h-[44px]"
            >
              <Send className="w-4 h-4 mr-2" />
              Broadcast
            </Button>
          )}

          {canEdit && (
            <Button
              variant="secondary"
              onClick={handleEdit}
              className="min-h-[44px]"
            >
              <Edit className="w-4 h-4 mr-2" />
              Edit
            </Button>
          )}

          {canDelete && (
            <Button
              variant="secondary"
              onClick={handleDelete}
              className="min-h-[44px] text-red-600 hover:text-red-700"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Delete
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
        {/* Content */}
        <div className="lg:col-span-2 space-y-4 sm:space-y-6">
          {/* Basic Information */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                {getTypeIcon(announcement.type)}
                <h2 className="text-lg font-semibold text-gray-900">
                  Announcement Details
                </h2>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Content
                </h3>
                <div className="prose prose-sm max-w-none">
                  <p className="text-gray-900 whitespace-pre-wrap">
                    {announcement.content}
                  </p>
                </div>
              </div>

              {announcement.summary && (
                <div>
                  <h3 className="text-sm font-medium text-gray-700 mb-2">
                    Summary
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {announcement.summary}
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Target Audience */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Users className="w-4 h-4" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Target Audience
                </h2>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {announcement.target_roles &&
                announcement.target_roles.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Target Roles
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {announcement.target_roles.map((role, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

              {announcement.target_grades &&
                announcement.target_grades.length > 0 && (
                  <div>
                    <h3 className="text-sm font-medium text-gray-700 mb-2">
                      Target Grades
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {announcement.target_grades.map((grade, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full"
                        >
                          Grade {grade}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
            </CardContent>
          </Card>

          {/* Delivery Channels */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Share2 className="w-4 h-4" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Delivery Channels
                </h2>
              </div>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {announcement.channels.map((channel, index) => (
                  <div
                    key={index}
                    className="flex items-center space-x-1 px-3 py-2 bg-gray-100 rounded-lg"
                  >
                    {getChannelIcon(channel)}
                    <span className="text-sm font-medium text-gray-700">
                      {channel.replace('_', ' ').toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 sm:space-y-6">
          {/* Status & Timing */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Status & Timing
                </h2>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Created</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatDate(announcement.created_at)}
                </span>
              </div>

              {announcement.scheduled_for && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Scheduled</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(announcement.scheduled_for)}
                  </span>
                </div>
              )}

              {announcement.expires_at && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Expires</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(announcement.expires_at)}
                  </span>
                </div>
              )}

              {announcement.approved_at && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Approved</span>
                  <span className="text-sm font-medium text-gray-900">
                    {formatDate(announcement.approved_at)}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Analytics */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Eye className="w-4 h-4" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Analytics
                </h2>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Total Recipients</span>
                <span className="text-sm font-medium text-gray-900">
                  {announcement.total_recipients.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Delivered</span>
                <span className="text-sm font-medium text-gray-900">
                  {announcement.delivery_count.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Read</span>
                <span className="text-sm font-medium text-gray-900">
                  {announcement.read_count.toLocaleString()}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Engagement</span>
                <span className="text-sm font-medium text-gray-900">
                  {announcement.engagement_count.toLocaleString()}
                </span>
              </div>
            </CardContent>
          </Card>

          {/* Metadata */}
          <Card>
            <CardHeader>
              <div className="flex items-center space-x-2">
                <Tag className="w-4 h-4" />
                <h2 className="text-lg font-semibold text-gray-900">
                  Metadata
                </h2>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Type</span>
                <span className="text-sm font-medium text-gray-900 capitalize">
                  {announcement.type}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Priority</span>
                <span className="text-sm font-medium text-gray-900 capitalize">
                  {announcement.priority}
                </span>
              </div>

              {announcement.tags && announcement.tags.length > 0 && (
                <div>
                  <span className="text-sm text-gray-600 block mb-2">Tags</span>
                  <div className="flex flex-wrap gap-1">
                    {announcement.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">FERPA Approved</span>
                <span
                  className={cn(
                    'text-sm font-medium',
                    announcement.ferpa_approved
                      ? 'text-green-600'
                      : 'text-red-600'
                  )}
                >
                  {announcement.ferpa_approved ? 'Yes' : 'No'}
                </span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-sm text-gray-600">Contains PII</span>
                <span
                  className={cn(
                    'text-sm font-medium',
                    announcement.contains_pii
                      ? 'text-red-600'
                      : 'text-green-600'
                  )}
                >
                  {announcement.contains_pii ? 'Yes' : 'No'}
                </span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title="Edit Announcement"
        size="2xl"
      >
        <AnnouncementForm
          announcement={announcement}
          onSubmit={handleFormSubmit}
          onCancel={() => setShowEditModal(false)}
          isLoading={isSubmitting}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Delete Announcement"
        size="md"
      >
        <div className="p-6">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
              <Trash2 className="h-6 w-6 text-red-600" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Delete Announcement
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete "{announcement.title}"? This
              action cannot be undone.
            </p>
            <div className="flex justify-center space-x-3">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteModal(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={handleConfirmDelete}
                disabled={isSubmitting}
                className="bg-red-600 hover:bg-red-700"
              >
                {isSubmitting ? 'Deleting...' : 'Delete'}
              </Button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
};
